import { Download, Search, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import type { RTPGrouping } from '@/types/rtpDownload';
import { RTP_GROUPING_LABELS, formatRTPDateTime } from '@/utils/rtpDownloadFormat';

export interface RTPDownloadActionsProps {
  // Preview
  hasPreviewData: boolean;
  isLoadingPreview: boolean;
  previewError: string | null;
  onPreview: () => void;
  canPreview: boolean;

  // Download
  selectedPreviewPatientIds: Set<string>;
  onDownload: () => void;
  isDownloading: boolean;
  isPreviewStale: boolean;

  // Config display
  timeRange: { from: string | null; to: string | null };
  grouping: RTPGrouping;
  patientCount: number;
  className?: string;
}

export function RTPDownloadActions({
  hasPreviewData,
  isLoadingPreview,
  previewError,
  onPreview,
  canPreview,
  selectedPreviewPatientIds,
  onDownload,
  isDownloading,
  isPreviewStale,
  timeRange,
  grouping,
  patientCount,
  className,
}: RTPDownloadActionsProps) {
  const canDownload =
    hasPreviewData &&
    !isPreviewStale &&
    selectedPreviewPatientIds.size > 0 &&
    !isDownloading;

  const showStaleWarning = isPreviewStale && hasPreviewData;

  return (
    <Card className={className}>
      <CardBody>
        <div className="space-y-4">
          <div>
            <h3 className="mb-3 text-sm font-semibold text-foreground">Download Summary</h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">Date Range:</span>
                <span className="font-medium text-foreground">
                  {timeRange.from && timeRange.to
                    ? `${formatRTPDateTime(timeRange.from)} - ${formatRTPDateTime(timeRange.to)}`
                    : 'Not selected'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted">Grouping:</span>
                <span className="font-medium text-foreground">
                  {RTP_GROUPING_LABELS[grouping]}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted">Patients:</span>
                <span className="font-medium text-foreground">
                  {patientCount === 0 ? 'All patients' : `${patientCount} selected`}
                </span>
              </div>

              {hasPreviewData && !isPreviewStale && (
                <div className="flex justify-between">
                  <span className="text-muted">Selected for download:</span>
                  <span className="font-medium text-foreground">
                    {selectedPreviewPatientIds.size} patient{selectedPreviewPatientIds.size !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>

          {showStaleWarning && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium">Preview is outdated</p>
                <p className="text-xs text-amber-600">
                  Inputs have changed. Please refresh preview before downloading.
                </p>
              </div>
            </div>
          )}

          {previewError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {previewError}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={onPreview}
              disabled={!canPreview}
              isLoading={isLoadingPreview}
              variant="outline"
              className="flex-1"
              data-testid="rtp-preview-button"
            >
              <Search className="mr-2 h-4 w-4" />
              {hasPreviewData ? 'Refresh Preview' : 'Generate Preview'}
            </Button>

            <Button
              onClick={onDownload}
              disabled={!canDownload}
              isLoading={isDownloading}
              className="flex-1"
              data-testid="rtp-download-button"
              title={
                isPreviewStale
                  ? 'Preview data is outdated - please refresh preview'
                  : !hasPreviewData
                  ? 'Please generate preview first'
                  : selectedPreviewPatientIds.size === 0
                  ? 'Please select at least one patient'
                  : undefined
              }
            >
              <Download className="mr-2 h-4 w-4" />
              Download RTP Data
            </Button>
          </div>

          {!timeRange.from || !timeRange.to ? (
            <p className="text-center text-xs text-muted">
              Please select a time range to enable preview
            </p>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}
